import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { notifyOk, notifyError } from "@/lib/notify";
import { useAuth } from "@/context/AuthContext";
import { capacidades } from "@/lib/capacidades";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Field } from "@/components/forms/fields";
import ZonaPeligro from "@/components/lifecycle/ZonaPeligro";
import ConfirmAccion from "@/components/lifecycle/ConfirmAccion";
import AccesoApp from "@/components/admin/AccesoApp";
import { Plus, Search } from "lucide-react";

const empty = { nombre: "", email: "", password: "", role: "monitorista" };

const ROLES_INTERNOS = ["monitorista", "supervisor"];
const ROLES_EXTERNOS = ["institucional_operador", "institucional_admin"];
const ROL_LABEL = {
  monitorista: "Monitorista",
  supervisor: "Supervisor",
  institucional_operador: "Operador institucional",
  institucional_admin: "Admin institucional",
};

const esInterno = (u) => (u.actor_type || "internal") === "internal";

export default function Users() {
  const { isSupervisor, user } = useAuth();
  const caps = capacidades(user);
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [tab, setTab] = useState("internos");
  const [q, setQ] = useState("");
  const [fOrg, setFOrg] = useState("todas");
  const [fSitio, setFSitio] = useState("todos");
  const [fRol, setFRol] = useState("todos");
  const [fEstado, setFEstado] = useState("activos");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [errores, setErrores] = useState({});
  const [detalle, setDetalle] = useState(null);
  const [confirmEstado, setConfirmEstado] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = () => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); };
  useEffect(() => {
    load();
    api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => setOrgs([]));
    api.get("/sitios").then((r) => setSitios(r.data)).catch(() => setSitios([]));
  }, []);

  const orgNombre = (id) => orgs.find((o) => o.id === id)?.nombre || "—";
  const sitioNombre = (id) => sitios.find((s) => s.id === id)?.nombre || id;

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    return users.filter((u) => {
      if (tab === "internos" && !esInterno(u)) return false;
      if (tab === "institucionales" && esInterno(u)) return false;
      if (fOrg !== "todas" && u.organization_id !== fOrg) return false;
      if (fSitio !== "todos" && !(u.allowed_site_ids || []).includes(fSitio)) return false;
      if (fRol !== "todos" && u.role !== fRol) return false;
      const inactivo = u.estado === "Inactivo";
      if (fEstado === "activos" && inactivo) return false;
      if (fEstado === "inactivos" && !inactivo) return false;
      if (t && !`${u.nombre || ""} ${u.email || ""}`.toLowerCase().includes(t)) return false;
      return true;
    });
  }, [users, tab, q, fOrg, fSitio, fRol, fEstado]);

  const rolesVisibles = tab === "internos" ? ROLES_INTERNOS
    : tab === "institucionales" ? ROLES_EXTERNOS : [...ROLES_INTERNOS, ...ROLES_EXTERNOS];

  const openNew = () => { setEditing(null); setF(empty); setErrores({}); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setF({ nombre: u.nombre || "", email: u.email, password: "", role: u.role });
    setErrores({});
    setOpen(true);
  };

  const save = async () => {
    const errs = {};
    if (!f.nombre.trim()) errs.nombre = "Escribe el nombre.";
    if (!editing && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) errs.email = "Correo inválido.";
    if (!editing && f.password.length < 10) errs.password = "Mínimo 10 caracteres.";
    setErrores(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, {
          nombre: f.nombre, role: f.role, password: f.password || undefined,
        });
      } else {
        await api.post("/users", { ...f, email: f.email.trim().toLowerCase() });
      }
      notifyOk("Usuario guardado.");
      setOpen(false);
      load();
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleEstado = async (u) => {
    try {
      if (u.estado === "Inactivo") await api.post(`/users/${u.id}/activar`);
      else await api.post(`/users/${u.id}/desactivar`);
      notifyOk(u.estado === "Inactivo" ? "Usuario activado." : "Usuario desactivado.");
      load();
      return true;
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
      return false;
    }
  };

  const sitiosResumen = (u) => {
    const ids = u.allowed_site_ids || [];
    if (!ids.length) return esInterno(u) ? "Todos (interno)" : "—";
    return ids.length === 1 ? sitioNombre(ids[0]) : `${ids.length} sitios`;
  };

  return (
    // El puente de tema nocturno (index.css · `.tema-noche-auto`) faltaba en esta raíz: sus 15
    // superficies `bg-white` (buscador, 4 filtros, tabla y botones) seguían blancas de noche
    // mientras el texto heredaba `--foreground` casi blanco. Misma corrección que Organizaciones.
    <div className="tema-noche-auto space-y-6" data-testid="usuarios-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#012472]">Usuarios</h1>
          <p className="text-sm text-slate-500 mt-1">
            Cuentas internas de Galarya y cuentas institucionales de los clientes.
          </p>
        </div>
        {caps.gestionarUsuarios && (
          <Button data-testid="add-user-button" onClick={openNew}
            className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white">
            <Plus strokeWidth={1.75} className="h-4 w-4 mr-1.5" /> Agregar usuario interno
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setFRol("todos"); }}>
        <TabsList className="rounded-xl flex-wrap h-auto">
          <TabsTrigger value="internos" data-testid="users-tab-internos"
            className="data-[state=active]:bg-[#012472] data-[state=active]:text-white">Internos</TabsTrigger>
          <TabsTrigger value="institucionales" data-testid="users-tab-institucionales"
            className="data-[state=active]:bg-[#012472] data-[state=active]:text-white">Institucionales</TabsTrigger>
          <TabsTrigger value="todos" data-testid="users-tab-todos"
            className="data-[state=active]:bg-[#012472] data-[state=active]:text-white">Todos</TabsTrigger>
          {isSupervisor && (
            <TabsTrigger value="acceso_app" data-testid="users-tab-acceso-app"
              className="data-[state=active]:bg-[#012472] data-[state=active]:text-white">Acceso App</TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {tab === "acceso_app" ? <AccesoApp /> : (
      <>
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="relative flex-1 max-w-md">
          <Search strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="users-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o correo…" className="pl-10 rounded-xl bg-white" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={fOrg} onValueChange={setFOrg}>
            <SelectTrigger data-testid="users-filter-organization" className="rounded-xl bg-white w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Toda organización</SelectItem>
              {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fSitio} onValueChange={setFSitio}>
            <SelectTrigger data-testid="users-filter-site" className="rounded-xl bg-white w-[170px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todo sitio</SelectItem>
              {sitios.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fRol} onValueChange={setFRol}>
            <SelectTrigger data-testid="users-filter-role" className="rounded-xl bg-white w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todo rol</SelectItem>
              {rolesVisibles.map((r) => <SelectItem key={r} value={r}>{ROL_LABEL[r]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fEstado} onValueChange={setFEstado}>
            <SelectTrigger data-testid="users-filter-estado" className="rounded-xl bg-white w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="activos">Sólo activos</SelectItem>
              <SelectItem value="inactivos">Sólo inactivos</SelectItem>
              <SelectItem value="todos">Incluir inactivos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="hidden md:block bg-white rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-gray-100">
              <th className="px-5 py-3 font-medium">Nombre</th>
              <th className="px-4 py-3 font-medium">Correo</th>
              <th className="px-4 py-3 font-medium">Tipo</th>
              <th className="px-4 py-3 font-medium">Rol</th>
              <th className="px-4 py-3 font-medium">Organización</th>
              <th className="px-4 py-3 font-medium">Sitios</th>
              <th className="px-4 py-3 font-medium">Estado</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtrados.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-400" data-testid="users-empty">
                No hay usuarios con estos filtros.
              </td></tr>
            )}
            {filtrados.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50/70">
                <td className="px-5 py-3 font-medium text-[#012472]">{u.nombre || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3 text-slate-500" data-testid={`user-tipo-${u.id}`}>
                  {esInterno(u) ? "Interno" : "Institucional"}
                </td>
                <td className="px-4 py-3 text-slate-500">{ROL_LABEL[u.role] || u.role}</td>
                <td className="px-4 py-3 text-slate-500">{esInterno(u) ? "—" : orgNombre(u.organization_id)}</td>
                <td className="px-4 py-3 text-slate-500" data-testid={`user-sitios-${u.id}`}>{sitiosResumen(u)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs rounded-full px-2.5 py-1 ${u.estado === "Inactivo" ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>
                    {u.estado === "Inactivo" ? "Inactivo" : "Activo"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 justify-end">
                    {esInterno(u) && (
                      <Button variant="outline" size="sm" className="rounded-lg"
                        onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>Editar</Button>
                    )}
                    {isSupervisor && (
                      <Button variant="ghost" size="sm" className="rounded-lg text-slate-500"
                        onClick={() => setDetalle(u)} data-testid={`gestionar-user-${u.id}`}>
                        Gestionar cuenta
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Vista compacta en móvil (mismos datos, sin scroll horizontal) */}
      <div className="md:hidden space-y-3">
        {filtrados.length === 0 && (
          <p className="text-slate-400 text-sm" data-testid="users-empty-mobile">
            No hay usuarios con estos filtros.
          </p>
        )}
        {filtrados.map((u) => (
          <div key={u.id} data-testid={`user-card-${u.id}`}
            className="bg-white rounded-2xl border border-gray-100 p-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-[#012472] truncate">{u.nombre || u.email}</p>
                <p className="text-xs text-slate-500 break-all">{u.email}</p>
              </div>
              <span className={`text-xs rounded-full px-2.5 py-1 shrink-0 ${u.estado === "Inactivo" ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>
                {u.estado === "Inactivo" ? "Inactivo" : "Activo"}
              </span>
            </div>
            <p className="text-xs text-slate-500">
              {esInterno(u) ? "Interno" : "Institucional"} · Rol: {ROL_LABEL[u.role] || u.role}
              {!esInterno(u) && ` · ${orgNombre(u.organization_id)}`}
            </p>
            <p className="text-xs text-slate-400">{sitiosResumen(u)}</p>
            <div className="flex flex-wrap gap-2 pt-1">
              {esInterno(u) && (
                <Button variant="outline" size="sm" className="rounded-lg"
                  onClick={() => openEdit(u)}>Editar</Button>
              )}
              {isSupervisor && (
                <Button variant="ghost" size="sm" className="rounded-lg text-slate-500"
                  onClick={() => setDetalle(u)} data-testid={`gestionar-user-card-${u.id}`}>
                  Gestionar cuenta
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      </>
      )}

      {/* Alta/edición de cuentas internas */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#012472]">{editing ? "Editar usuario" : "Nuevo usuario interno"}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Las cuentas institucionales se crean desde la organización correspondiente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Nombre" error={errores.nombre}>
              <Input className="rounded-xl" value={f.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="user-nombre-input" />
            </Field>
            <Field label="Correo" error={errores.email}>
              <Input type="email" inputMode="email" autoComplete="email" className="rounded-xl"
                value={f.email} disabled={!!editing}
                onChange={(e) => set("email", e.target.value)} data-testid="user-email-input" />
            </Field>
            <Field label={editing ? "Nueva contraseña (opcional)" : "Contraseña"} error={errores.password}>
              <Input type="password" className="rounded-xl" value={f.password}
                onChange={(e) => set("password", e.target.value)} data-testid="user-password-input" />
            </Field>
            <Field label="Rol">
              <Select value={f.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className="rounded-xl" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monitorista">Monitorista</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="user-guardar-button" disabled={saving}
              className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white" onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gestión de una cuenta: estado y acceso + avanzado */}
      <Dialog open={!!detalle} onOpenChange={(v) => { if (!v) { setDetalle(null); setConfirmEstado(false); } }}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#012472] break-words">{detalle?.email}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Desactivar retira el acceso y conserva el historial.
            </DialogDescription>
          </DialogHeader>
          {detalle && (
            <div className="space-y-6">
              <section className="space-y-3">
                <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Estado y acceso</p>
                <div className="flex items-center gap-2">
                  <span data-testid="user-estado-actual"
                    className={`text-xs rounded-full px-2.5 py-1 ${detalle.estado === "Inactivo" ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>
                    {detalle.estado === "Inactivo" ? "Inactivo" : "Activo"}
                  </span>
                </div>
                <Button variant="outline" className="rounded-xl w-full min-h-[44px]"
                  data-testid="lifecycle-toggle-estado" onClick={() => setConfirmEstado(true)}>
                  {detalle.estado === "Inactivo" ? "Activar usuario" : "Desactivar usuario"}
                </Button>
              </section>
              <section className="space-y-3">
                <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Avanzado</p>
                <ZonaPeligro basePath={`/users/${detalle.id}`} tipoLabel="usuario"
                  labelToggle="Eliminar usuario permanentemente"
                  confirmarCon="el correo exacto" confirmarValor={detalle.email}
                  testPrefix="user" onDeleted={() => { setDetalle(null); load(); }} />
              </section>
              <ConfirmAccion open={confirmEstado} onOpenChange={setConfirmEstado} testPrefix="user"
                titulo={`${detalle.estado === "Inactivo" ? "Activar" : "Desactivar"} ${detalle.nombre || detalle.email}`}
                texto={detalle.estado === "Inactivo"
                  ? "Esta cuenta podrá volver a iniciar sesión según su rol y permisos."
                  : "Esta cuenta perderá acceso inmediatamente. Su historial se conservará."}
                confirmLabel={detalle.estado === "Inactivo" ? "Activar usuario" : "Desactivar usuario"}
                onConfirm={async () => {
                  const ok = await toggleEstado(detalle);
                  if (ok !== false) { setConfirmEstado(false); setDetalle(null); }
                }} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
